/* global ODADMIN_URL */
/* global OPERATOR_HISTORY_END_BUFFER */
/* global OPERATOR_HISTORY_PAGE_MINS */

import moment from 'moment';
import _ from 'underscore';

import BaseApi from 'common/api/base';
import {startRequest} from 'common/automationUtils';

export const RIDE_FETCH_WINDOW_HOURS = 4;
export const USER_GROUP_ROW_PAGE_LIMIT = 25;

export default class OdAdminApi extends BaseApi {
  constructor() {
    super();
    this.baseUrl = ODADMIN_URL;
  }

  fetchAgencySettings({agency}) {
    return this.fetch('GET', `${agency}/settings`);
  }

  saveAgencySetting(settings, {agency}) {
    return this.fetch('PUT', `${agency}/settings`, settings);
  }

  fetchAgencyAddresses({agency}) {
    return this.fetch('GET', `${agency}/addresses`);
  }

  saveAgencyAddress(address, {agency}) {
    const endpoint = `${agency}/addresses/${address.address_id || ''}`;
    return this.fetch(address.address_id ? 'PUT' : 'POST', endpoint, address);
  }

  deleteAgencyAddress(addressId, {agency}) {
    return this.fetch('DELETE', `${agency}/addresses/${addressId}`);
  }

  fetchAgencyVehicles({agency, options}) {
    const params = options && options.showLastActiveAt ? '?show_last_active_at=true' : '';
    return this.fetch('GET', `${agency}/vehicles${params}`);
  }

  fetchAgencyVehiclePositions({agency}) {
    return this.fetch('GET', `${agency}/vehicles/positions`);
  }

  saveAgencyVehicle(vehicle, {agency}) {
    const endpoint = `${agency}/vehicles/${vehicle.vehicle_id || ''}`;
    return this.fetch(vehicle.vehicle_id ? 'PUT' : 'POST', endpoint, vehicle);
  }

  setAgencyVehicleState(eligible, {agency, vehicleId}) {
    const endpoint = `${agency}/vehicles/${vehicleId}/eligibility`;
    return this.fetch('POST', endpoint, {
      eligible,
    });
  }

  deleteAgencyVehicle(vehicleId, {agency}) {
    return this.fetch('DELETE', `${agency}/vehicles/${vehicleId}`);
  }

  fetchAgencyDevices({agency}) {
    return this.fetch('GET', `${agency}/devices`);
  }

  fetchServices({agency}) {
    return this.fetch('GET', `${agency}/services`);
  }

  fetchService(serviceId, {agency}) {
    return this.fetch('GET', `${agency}/services/${serviceId}`);
  }

  saveService(service, {agency}) {
    const endpoint = `${agency}/services/${service.service_id || ''}`;
    return this.fetch(service.service_id ? 'PUT' : 'POST', endpoint, service);
  }

  deleteService(serviceId, {agency}) {
    return this.fetch('DELETE', `${agency}/services/${serviceId}`);
  }

  fetchAgencyUsers({agency}) {
    return this.fetch('GET', `${agency}/users`);
  }

  fetchAgencyGroups({agency}) {
    return this.fetch('GET', `${agency}/groups`);
  }

  saveAgencyGroup(group, {agency}) {
    const endpoint = `${agency}/groups/${group.group_id || ''}`;
    return this.fetch(group.group_id ? 'PUT' : 'POST', endpoint, group);
  }

  deleteAgencyGroup(groupId, {agency}) {
    return this.fetch('DELETE', `${agency}/groups/${groupId}`);
  }

  fetchGroupUsers({agency, group, nextCursor, email}) {
    let endpoint = `${agency}/groups/${group}/users?limit=${USER_GROUP_ROW_PAGE_LIMIT}`;
    if (!_.isEmpty(email)) {
      endpoint += `&email=${email}`;
    } else if (!_.isEmpty(nextCursor)) {
      endpoint += `&next_cursor=${nextCursor}`;
    }
    return this.fetch('GET', endpoint);
  }

  saveGroupUser(username, {agency, group}) {
    const endpoint = `${agency}/groups/${group}/users/${username}`;
    return this.fetch('POST', endpoint);
  }

  deleteGroupUser(username, {agency, group}) {
    return this.fetch('DELETE', `${agency}/groups/${group}/users/${username}`);
  }

  _multipartFetch(method, endpoint, data) {
    const url = `${this.baseUrl}${endpoint}`;

    const params = {...this.defaultParams};

    // Explicitly setting content-type causes issues because the manual version lacks appropriate
    // form boundaries. Fetch and XMLHttpRequest will automatically set the content-type,
    // content-disposition and boundaries in accordance with RFC-7578.
    // https://tools.ietf.org/html/rfc7578
    delete params.headers['Content-Type'];

    const request = startRequest(url);
    return fetch(url, {
      method,
      body: data || null,
      ...params,
    }).then((...args) => {
      request.end();
      return Promise.resolve(...args);
    }, (...args) => {
      request.end();
      return Promise.reject(...args);
    });
  }

  uploadGroupUsers(formData, {agency, group}) {
    const endpoint = `${agency}/groups/${group}/users/upload`;
    return this._multipartFetch('POST', endpoint, formData);
  }

  fetchAgencyRegions({agency}) {
    return this.fetch('GET', `${agency}/regions`);
  }

  saveAgencyRegion(region, {agency}) {
    const endpoint = `${agency}/regions/${region.region_id || ''}`;
    return this.fetch(region.region_id ? 'PUT' : 'POST', endpoint, region);
  }

  deleteAgencyRegion(regionId, {agency}) {
    return this.fetch('DELETE', `${agency}/regions/${regionId}`);
  }

  _fetchRides(agency, startTime, endTime, cursor, suffix = '') {
    const params = [
      cursor ? `page.cursor=${cursor}` : null,
      `start_time=${startTime.toISOString()}`,
      `end_time=${endTime.toISOString()}`,
    ];
    return this.fetch('GET', `${agency}/rides?${_.compact(params).join('&')}${suffix}`);
  }

  fetchDispatchRides({agency, datetime}, cursor) {
    const startTime = moment(datetime);
    const now = moment(agency.timezone);
    const midnightTonight = now.clone().endOf('day');
    const endTime =
      midnightTonight.diff(now) <= moment.duration(RIDE_FETCH_WINDOW_HOURS, 'hours')
        ? now.add(moment.duration(RIDE_FETCH_WINDOW_HOURS, 'hours'))
        : midnightTonight;

    return this._fetchRides(agency, startTime, endTime, cursor, '&include_histories=true');
  }

  // TODO(dakcrowder) startTime and endTime should be localized based on agency timezone
  fetchAgencyRides({agency, date}, cursor) {
    const startTime = moment(date).startOf('day');
    const endTime = startTime.clone().add(24, 'hours');
    return this._fetchRides(agency, startTime, endTime, cursor);
  }

  fetchVehicleSchedules({agency}) {
    return this.fetch('GET', `${agency}/schedules`);
  }

  fetchAgencyRide(id, {agency}) {
    const endpoint = `${agency}/rides/${id}`;
    return this.fetch('GET', endpoint);
  }

  approveAgencyRide(agencyId, rideId) {
    return this.fetch('POST', `${agencyId}/rides/${rideId}/approve`);
  }

  cancelAgencyRide(agencyId, rideId, terminalReason) {
    return this.fetch('POST', `${agencyId}/rides/${rideId}/cancel`, {
      terminal_reason: terminalReason,
    });
  }

  denyAgencyRide(agencyId, rideId, reason) {
    return this.fetch('POST', `${agencyId}/rides/${rideId}/deny`, {
      terminal_reason: reason,
    });
  }

  prioritizeAgencyRide(agencyId, rideId, priorityInfo) {
    let endpoint = `${agencyId}/rides/${rideId}/prioritize`;
    if (priorityInfo) {
      if (priorityInfo.pickup) {
        endpoint += '/pickup';
      } else if (priorityInfo.dropoff) {
        endpoint += '/dropoff';
      }
    }

    let payload = null;
    if (priorityInfo.vehicleId) {
      payload = {vehicle_id: priorityInfo.vehicleId};
    }

    return this.fetch('POST', endpoint, payload);
  }

  saveAgencyRide(ride, {agency}) {
    const actualRide = Object.assign({}, ride, {
      origin: null,
      destination: null,
    });

    // Saving an existing ride
    if (ride.ride_id) {
      return this.fetch('PUT', `${agency}/rides/${ride.ride_id}`, actualRide);
    }

    // Creating a new ride
    return this.fetch('POST', `${agency}/rides`, actualRide);
  }

  saveRideSubscription(rideSubscriptionDraft, {agencyId}) {
    const actualRide = Object.assign({}, rideSubscriptionDraft.ride, {
      origin: null,
      destination: null,
    });
    return this.fetch('POST', `${agencyId}/rides/subscriptions`, {
      ride: actualRide,
      rides: rideSubscriptionDraft.rides,
      description: rideSubscriptionDraft.description,
    });
  }

  fetchRideSubscriptions({agency, endTime}, cursor) {
    let params = '';
    if (endTime) {
      params += `?end_time=${endTime}`;
    }
    if (cursor) {
      params += `&page.cursor=${cursor}`;
    }
    return this.fetch('GET', `${agency}/rides/subscriptions${params}`);
  }

  // We use the same endpoint to populate the completedRideSubscriptions store as rideSubscriptions
  // but want to use the common createFetchListActions.  Hence this.
  fetchCompletedRideSubscriptions = this.fetchRideSubscriptions;

  saveRideSubscriptionItem(agency, subscriptionId, rideSubscriptionItem) {
    const itemId = rideSubscriptionItem.ride_subscription_item_id;
    return this.fetch('PUT', `${agency}/rides/subscriptions/${subscriptionId}/items/${itemId}`, {
      status: rideSubscriptionItem.status,
      timestamp: rideSubscriptionItem.timestamp,
    });
  }

  cancelRideSubscriptionItemsAll(agency, subscriptionId) {
    return this.fetch('PUT', `${agency}/rides/subscriptions/${subscriptionId}/cancel`);
  }

  fetchServiceRiders({agency, service}) {
    const endpoint = `${agency}/services/${service}/riders`;
    return this.fetch('GET', endpoint);
  }

  saveServiceRider(username, {agency, service}) {
    const endpoint = `${agency}/services/${service}/riders/${username}`;
    return this.fetch('POST', endpoint);
  }

  deleteServiceRider(username, {agency, service}) {
    return this.fetch('DELETE', `${agency}/services/${service}/riders/${username}`);
  }

  fetchReport({agency, reportType, startDate, endDate, serviceIds}, cursor) {
    const cursorQuery = cursor ? `&page.cursor=${cursor}` : '';
    const serviceIdsQuery = serviceIds
      ? _.map(serviceIds, (sId) => `&service_ids=${sId}`).join('')
      : '';
    const startTime = startDate.toISOString();
    const endTime = endDate.clone().add(1, 'day').toISOString();
    const endpoint =
      `${agency}/reports/${reportType}?start_date=${startTime}` +
      `&end_date=${endTime}${serviceIdsQuery}${cursorQuery}`;
    return this.fetch('GET', endpoint);
  }

  fetchOperatorReport({agency, date, username}) {
    const endpoint = `${agency}/reports/operators/${username}?start_date=${date}`;
    return this.fetch('GET', endpoint);
  }

  fetchAggregateOperatorReport({agency, date}) {
    const endpoint = `${agency}/reports/operators?start_date=${date}`;
    return this.fetch('GET', endpoint);
  }

  fetchOperatorHistoryVehiclePositions(agency, dateTime) {
    const startTime = dateTime.toISOString();
    const endTime = dateTime.clone().add(OPERATOR_HISTORY_PAGE_MINS, 'minute').toISOString();
    const endpoint =
      `${agency}/reports/operator-history/vehicle-positions?` +
        `start_time=${startTime}&end_time=${endTime}`;
    return this.fetch('GET', endpoint);
  }

  fetchOperatorHistoryVehicleHistory(agency, dateTime) {
    const startTime = dateTime.toISOString();
    const endTime = dateTime.clone().add(OPERATOR_HISTORY_END_BUFFER, 'minute').toISOString();
    const endpoint =
      `${agency}/reports/operator-history/vehicle-history?` +
        `start_time=${startTime}&end_time=${endTime}`;
    return this.fetch('GET', endpoint);
  }

  fetchOperatorHistoryRideDetails(agency, dateTime) {
    const startTime = dateTime.toISOString();
    const endTime = dateTime.clone().add(OPERATOR_HISTORY_PAGE_MINS, 'minute').toISOString();
    const endpoint =
      `${agency}/reports/operator-history/ride-details?` +
        `start_time=${startTime}&end_time=${endTime}`;
    return this.fetch('GET', endpoint);
  }

  fetchRideExport({agency, startDate, endDate}) {
    const startTime = startDate.toISOString();
    const endTime = endDate.clone().add(1, 'day').toISOString();
    const endpoint = `${agency}/rides/export?start_date=${startTime}&end_date=${endTime}`;
    return this.fetch('POST', endpoint);
  }

  fetchNtdReport({agency, startDate, endDate, apiProps}) {
    const startTime = startDate.toISOString();
    const endTime = endDate.clone().add(1, 'day').toISOString();
    const additionalProps = apiProps.byService ? '&by_service=true' : '';
    const endpoint =
      `${agency}/reports/ntd?start_date=${startTime}&end_date=${endTime}${additionalProps}`;
    return this.fetch('POST', endpoint);
  }

  fetchOperatorMetricsExport({agency, startDate, endDate}) {
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');
    const endpoint = `${agency}/reports/operators/export?start_date=${start}&end_date=${end}`;
    return this.fetch('POST', endpoint);
  }

  fetchPerformanceMetricsExport({agency, startDate, endDate, serviceIds}) {
    const start = startDate.toISOString();
    const end = endDate.clone().add(1, 'day').toISOString();
    const serviceIdArg = serviceIds == null ? '' : serviceIds;
    const endpoint =
      `${agency}/reports/performance-metrics/export?start_date=${start}&end_date=${end}` +
      `&service_ids=${serviceIdArg}`;
    return this.fetch('POST', endpoint);
  }

  fetchFarePaymentExport({agency, startDate, endDate, serviceIds}) {
    const start = startDate.toISOString();
    const end = endDate.clone().add(1, 'day').toISOString();
    const serviceIdArg = serviceIds == null ? '' : serviceIds;
    const endpoint =
      `${agency}/reports/fare-payments/export?start_date=${start}&end_date=${end}` +
      `&service_ids=${serviceIdArg}`;
    return this.fetch('POST', endpoint);
  }

  fetchSystemHealthReport({agency, datetime, serviceIds}) {
    const serviceIdsQuery = serviceIds
      ? _.map(serviceIds, (id) => `&service_ids=${id}`).join('')
      : '';
    const endpoint =
      `${agency}/reports/system-health?datetime=${datetime.toISOString()}${serviceIdsQuery}`;
    return this.fetch('GET', endpoint);
  }
}



// WEBPACK FOOTER //
// ./src/common/api/OdAdminApi.js